import { useEffect, useState } from 'react';
import type {
  EjecucionVisita as Ejecucion,
  PlantillaResumen,
  ResultadoItem,
} from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { fechaLarga } from '../componentes.js';
import { Modal } from './Modal.js';
import { Icono } from './Iconos.js';

const RESULTADOS: ResultadoItem[] = ['OK', 'INCIDENCIA', 'NO_APLICA'];

const CLASE: Record<ResultadoItem, string> = {
  PENDIENTE: 'chip-area',
  OK: 'chip-foto',
  INCIDENCIA: 'chip-sla',
  NO_APLICA: 'chip-area',
};

/**
 * Ejecución de una visita: la lista copiada de la plantilla, marcable ítem a
 * ítem. Al completar, lo marcado como incidencia abre incidencias reales, y la
 * pantalla dice cuántas ha abierto en vez de cerrarse en silencio.
 */
/**
 * LA NOTA DE UN PUNTO.
 *
 * Mientras no hay nada escrito es un enlace pequeño: veinte cajas de texto
 * vacías en una lista de veinte puntos la vuelven ilegible. En cuanto hay algo,
 * se lee siempre, porque eso es lo que alguien viene a buscar meses después.
 *
 * Guarda al salir del campo. Un botón de guardar por punto serían cuarenta
 * cosas que tocar en una lista de veinte.
 */
function NotaDelPunto({
  idioma,
  valor,
  onGuardar,
}: {
  idioma: Idioma;
  valor: string | null;
  onGuardar: (texto: string) => void;
}) {
  const [abierta, setAbierta] = useState(false);
  const [texto, setTexto] = useState(valor ?? '');

  // Si la nota cambia por fuera —otra pestaña, otro dispositivo— se adopta.
  useEffect(() => {
    setTexto(valor ?? '');
  }, [valor]);

  if (!abierta && !valor) {
    return (
      <button type="button" className="enlace nota-punto-anadir" onClick={() => setAbierta(true)}>
        + {t(idioma, 'visita.anadirNota')}
      </button>
    );
  }

  if (!abierta) {
    return (
      <span className="nota-punto">
        {valor}{' '}
        <button type="button" className="enlace" onClick={() => setAbierta(true)}>
          {t(idioma, 'app.editar')}
        </button>
      </span>
    );
  }

  return (
    <textarea
      className="nota-punto-campo"
      rows={2}
      autoFocus
      value={texto}
      placeholder={t(idioma, 'visita.notaPie')}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={() => {
        setAbierta(false);
        if ((texto.trim() || null) !== (valor ?? null)) onGuardar(texto);
      }}
    />
  );
}

export function PanelEjecucion({
  idioma,
  ejecucion: inicial,
  casa,
  onCambio,
  onSalir,
}: {
  idioma: Idioma;
  ejecucion: Ejecucion;
  onCambio: () => void;
  /** La casa que se esta recorriendo: aqui ya no se ve la ficha. */
  casa?: string;
  /** Volver a la lista de visitas de la casa. */
  onSalir: () => void;
}) {
  /*
    MARCAR UN PUNTO NO RECARGA LA PANTALLA.

    Antes, cada «OK» volvia a pedir la lista entera y la pantalla se redibujaba
    desde cero: en el movil eso devuelve al principio de la pagina, y recorrer
    veinte puntos obligaba a bajar veinte veces. La respuesta de marcar YA trae
    la visita actualizada, asi que se usa esa y no se mueve nada mas.

    Se avisa al resto de la pantalla —`onCambio`— solo al completar, que es
    cuando cambia algo fuera de aqui: el evento de la agenda.
  */
  const [ejecucion, setEjecucion] = useState<Ejecucion>(inicial);
  const [notas, setNotas] = useState('');
  const [resultado, setResultado] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** El punto cuya foto se esta subiendo, para no dejarlo sin señal. */
  const [subiendo, setSubiendo] = useState<string | null>(null);
  const enCurso = ejecucion.estado === 'EN_CURSO';

  /**
   * SALIR.
   *
   * De una visita ya cerrada se sale sin mas: se estaba consultando. De una
   * EMPEZADA no: salir es darla por no hecha, y eso se pregunta, porque lo
   * marcado hasta ahi se pierde.
   */
  async function salir(): Promise<void> {
    if (!enCurso) {
      onSalir();
      return;
    }
    if (!window.confirm(t(idioma, 'visita.confirmarDescartar'))) return;
    setError(null);
    try {
      await api.descartarVisita(ejecucion.id);
      onCambio();
      onSalir();
    } catch (e) {
      setError(e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, 'error.servidor'));
    }
  }

  async function marcar(itemId: string, res: ResultadoItem): Promise<void> {
    setError(null);
    try {
      const { ejecucion: actualizada } = await api.marcarItemVisita(ejecucion.id, itemId, res);
      setEjecucion(actualizada);
    } catch (e) {
      setError(e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, 'error.servidor'));
    }
  }

  /** La foto de un punto: se sube y se queda colgada de EL, no de la visita. */
  async function subirFotos(itemId: string, ficheros: File[]): Promise<void> {
    setError(null);
    setSubiendo(itemId);
    try {
      // Una detras de otra y no a la vez: diez fotos de golpe desde el movil
      // saturan la subida y fallan varias sin decir cual.
      for (const fichero of ficheros) {
        await api.subirAdjunto('VISITA_ITEM', itemId, fichero);
      }
      const { ejecucion: actualizada } = await api.ejecucion(ejecucion.id);
      setEjecucion(actualizada);
    } catch (e) {
      setError(e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, 'error.servidor'));
    } finally {
      setSubiendo(null);
    }
  }

  /**
   * LO QUE SE ESCRIBE EN UN PUNTO.
   *
   * «Visita por motivo específico» es una lista de UN punto: todo lo que esa
   * visita tiene que contar es lo que se escriba ahí. No había dónde
   * escribirlo —sólo OK, Incidencia y No aplica— así que la visita se cerraba
   * sin decir a qué se había ido.
   *
   * Se guarda al salir del campo, no con un botón: en el móvil, un botón de
   * guardar por cada punto de una lista de veinte es una lista de cuarenta
   * cosas que tocar.
   */
  async function anotarItem(itemId: string, texto: string): Promise<void> {
    setError(null);
    try {
      const { ejecucion: actualizada } = await api.anotarItemVisita(
        ejecucion.id,
        itemId,
        texto.trim() || null,
      );
      setEjecucion(actualizada);
    } catch (e) {
      setError(e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, 'error.servidor'));
    }
  }

  /** La nota de la visita entera. Tambien con la visita ya cerrada. */
  async function anotarLaVisita(texto: string): Promise<void> {
    setError(null);
    try {
      const { ejecucion: actualizada } = await api.anotarVisita(ejecucion.id, texto.trim() || null);
      setEjecucion(actualizada);
    } catch (e) {
      setError(e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, 'error.servidor'));
    }
  }

  /** Quitar una foto: la del dedo, la del suelo, la repetida. */
  async function quitarFoto(adjuntoId: string): Promise<void> {
    if (!window.confirm(t(idioma, 'visita.confirmarQuitarFoto'))) return;
    setError(null);
    try {
      await api.eliminarAdjunto(adjuntoId);
      const { ejecucion: actualizada } = await api.ejecucion(ejecucion.id);
      setEjecucion(actualizada);
    } catch (e) {
      setError(e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, 'error.servidor'));
    }
  }

  const pendientes = ejecucion.items.filter((i) => i.resultado === 'PENDIENTE').length;
  const problemas = ejecucion.items.filter((i) => i.resultado === 'INCIDENCIA').length;

  const secciones = [...new Set(ejecucion.items.map((i) => i.seccion))];

  return (
    <section className="tarjeta pantalla-visita">
      {/*
        EL ATRAS, ARRIBA Y SOLO. Es la unica salida que no es cerrar la visita,
        y dice lo que hace: en una visita empezada, descartarla.
      */}
      <button type="button" className="secundario pequeno atras-visita" onClick={() => void salir()}>
        <Icono nombre="volver" /> {t(idioma, enCurso ? 'visita.descartar' : 'app.volver')}
      </button>
      <div className="tarjeta-cabeza">
        <h2>
          {casa ? <span className="casa-de-la-visita">{casa}</span> : null}
          {ejecucion.plantillaNombre ?? t(idioma, 'visita.sinPlantilla')}
        </h2>
        <span className={`pastilla ${enCurso ? 'ambar' : 'verde'}`}>
          {t(idioma, `visita.estado${ejecucion.estado}`)}
        </span>
      </div>
      <p className="suave" style={{ fontSize: '0.8125rem' }}>
        {ejecucion.eventoId ? `${t(idioma, 'visita.deLaAgenda')} · ` : ''}
        {fechaLarga(ejecucion.iniciadaEn, idioma)}
        {ejecucion.ejecutorNombre ? ` · ${ejecucion.ejecutorNombre}` : ''}
        {ejecucion.items.length > 0
          ? ` · ${t(idioma, 'visita.pendientes', { total: pendientes })}`
          : ''}
      </p>

      {resultado && (
        <p className="aviso-demo" role="status">
          {resultado}
        </p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {ejecucion.items.length === 0 && (
        <p className="suave">{t(idioma, 'visita.sinItems')}</p>
      )}

      {/*
        LA NOTA DE LA VISITA, DONDE SE LEE.

        Se escribia al cerrar y no volvia a verse nunca mas: quedaba guardada en
        la base de datos y fuera de la pantalla, que desde fuera no se distingue
        de no haberla guardado. Aqui esta, y se puede corregir despues: acordarse
        de algo al llegar al coche es lo normal.
      */}
      <div className="campo nota-visita">
        <label htmlFor={`nota-visita-${ejecucion.id}`}>{t(idioma, 'visita.notaVisita')}</label>
        <NotaDelPunto
          idioma={idioma}
          valor={ejecucion.notas}
          onGuardar={(texto) => void anotarLaVisita(texto)}
        />
      </div>

      {secciones.map((sec) => (
        <details key={sec} className="plegable" open>
          <summary>
            {sec.toUpperCase()}{' '}
            <span className="chip-area">
              {ejecucion.items.filter((i) => i.seccion === sec).length}
            </span>
          </summary>
          <div className="cuerpo">
            <ol className="items-plantilla">
              {ejecucion.items
                .filter((i) => i.seccion === sec)
                .map((i) => (
                  <li key={i.id}>
                    <span className="texto-item">
                      <b>{i.texto}</b>
                      {i.critico && <i>{t(idioma, 'checklists.critico')}</i>}
                      {/*
                        La foto va DELANTE de las marcas cuando hace falta: es
                        lo que hay que hacer antes de poder dar el punto por
                        bueno, y esconderlo detras seria una trampa.
                      */}
                      {i.exigeFoto && i.fotos.length === 0 && (
                        <i className="pide-foto">{t(idioma, 'visita.pideFoto')}</i>
                      )}
                      {i.fotos.length > 0 && (
                        <span className="fotos-item">
                          {i.fotos.map((f) => (
                            <span key={f.id} className="foto-item">
                              <a href={api.urlAdjunto(f.id)} target="_blank" rel="noreferrer">
                                <img src={api.urlAdjunto(f.id)} alt={f.nombreFichero} />
                              </a>
                              {/*
                                QUITARLA. La del dedo tapando el objetivo, la
                                del suelo, la repetida. Sin esto, una foto mal
                                hecha se queda en el acta para siempre.
                              */}
                              <button
                                type="button"
                                className="quitar-foto"
                                title={t(idioma, 'visita.quitarFoto')}
                                aria-label={t(idioma, 'visita.quitarFoto')}
                                onClick={() => void quitarFoto(f.id)}
                              >
                                <Icono nombre="papelera" />
                              </button>
                            </span>
                          ))}
                        </span>
                      )}
                      {/*
                        LA NOTA DEL PUNTO, SIEMPRE A LA VISTA. Se escribe
                        mientras se recorre y se lee meses despues, que es
                        cuando alguien pregunta que se encontro aquel dia.
                      */}
                      <NotaDelPunto
                        idioma={idioma}
                        valor={i.notas}
                        onGuardar={(texto) => void anotarItem(i.id, texto)}
                      />
                    </span>
                    <span className="marcas">
                      {/*
                        CUANDO LA FOTO ES OBLIGATORIA Y NO ESTA, EL BOTON PESA.

                        Era un chip gris mas, el cuarto de la fila, entre «OK» y
                        «Incidencia»: se pulsaba OK, saltaba «este punto pide una
                        foto» y no se veia por ninguna parte con que hacerla.
                        Ahora, mientras falte, es el boton llamativo de la fila y
                        dice lo que hay que hacer; cuando ya esta, vuelve a ser
                        un chip discreto para poder anadir otra.
                      */}
                      {enCurso && (
                        <label
                          className={
                            (i.exigeFoto && i.fotos.length === 0
                              ? 'chip-foto-subir hace-falta'
                              : 'chip-area chip-foto-subir') + (subiendo === i.id ? ' subiendo' : '')
                          }
                        >
                          <Icono nombre="camara" />{' '}
                          {t(
                            idioma,
                            subiendo === i.id
                              ? 'visita.subiendoFoto'
                              : i.exigeFoto && i.fotos.length === 0
                                ? 'visita.hacerFoto'
                                : 'visita.anadirFoto',
                          )}
                          {/*
                            SIN `capture`: la camara Y el carrete.

                            Con `capture="environment"` el movil abria la camara
                            directamente y no dejaba otra cosa. Pero la foto del
                            cuadro electrico se hizo muchas veces antes de
                            entrar a la lista, y entonces no habia manera de
                            subirla. Sin ese atributo, el telefono pregunta: usar
                            la camara, o elegir del carrete.
                          */}
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const ficheros = [...(e.target.files ?? [])];
                              e.target.value = '';
                              if (ficheros.length > 0) void subirFotos(i.id, ficheros);
                            }}
                          />
                        </label>
                      )}
                      {enCurso ? (
                        RESULTADOS.map((r) => (
                          <button
                            key={r}
                            type="button"
                            className={i.resultado === r ? CLASE[r] : 'chip-area'}
                            onClick={() => void marcar(i.id, r)}
                          >
                            {t(idioma, `visita.res${r}`)}
                          </button>
                        ))
                      ) : (
                        <span className={CLASE[i.resultado]}>
                          {t(idioma, `visita.res${i.resultado}`)}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
            </ol>
          </div>
        </details>
      ))}

      {/*
        UNA VISITA CERRADA DE MAS SE PUEDE ABRIR.

        Antes no: la lista pasaba a solo lectura y un punto sin resolver se
        quedaba asi para siempre, sin forma de hacerle la foto que le faltaba.
      */}
      {!enCurso && (
        <button
          type="button"
          className="secundario"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => {
            setError(null);
            void api
              .reabrirVisita(ejecucion.id)
              .then(({ ejecucion: abierta }) => {
                setEjecucion(abierta);
                setResultado(null);
                onCambio();
              })
              .catch((e: unknown) =>
                setError(
                  e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, 'error.servidor'),
                ),
              );
          }}
        >
          {t(idioma, 'visita.reabrir')}
        </button>
      )}
      {/*
        Y PARA QUE SIRVE. Un boton que dice «Reabrir la visita» en una visita ya
        cerrada no explica por que lo pulsaria nadie; esta linea si.
      */}
      {!enCurso && (
        <p className="suave" style={{ fontSize: '0.8125rem', marginTop: '0.25rem' }}>
          {t(idioma, 'visita.reabrirPie')}
        </p>
      )}

      {enCurso && (
        <form
          className="formulario"
          style={{ maxWidth: 'none' }}
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            void api
              .completarVisita(ejecucion.id, notas.trim() || undefined)
              .then((r) => {
                setResultado(
                  r.incidenciasCreadas > 0
                    ? t(idioma, 'visita.completadaCon', { total: r.incidenciasCreadas })
                    : t(idioma, 'visita.completadaSin'),
                );
                setNotas('');
                setEjecucion(r.ejecucion);
                onCambio();
              })
              .catch((e: unknown) =>
                setError(
                  e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, 'error.servidor'),
                ),
              );
          }}
        >
          <div className="campo">
            <label htmlFor={`vis-notas-${ejecucion.id}`}>{t(idioma, 'evento.notas')}</label>
            <textarea
              id={`vis-notas-${ejecucion.id}`}
              rows={2}
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
            />
          </div>
          <button type="submit" style={{ alignSelf: 'flex-start' }}>
            ✓ {t(idioma, 'visita.completar')}
            {problemas > 0 ? ` · ${t(idioma, 'visita.abrira', { total: problemas })}` : ''}
          </button>
        </form>
      )}
    </section>
  );
}

/** Diálogo para iniciar una visita, con o sin plantilla. */
/**
 * Empezar una visita.
 *
 * Se llega aqui de dos maneras y las dos acaban igual: con la lista de
 * comprobacion abierta. Desde el boton «Iniciar visita», para la vuelta que no
 * estaba planeada; y PULSANDO UNA VISITA PROGRAMADA de la agenda, que es lo
 * normal —se programa el lunes, se recorre el jueves— y lo que faltaba: la
 * agenda solo dejaba cambiarle el estado a mano, sin abrir nada.
 *
 * Cuando viene de la agenda, la visita ejecutada queda ATADA a ese evento: al
 * cerrarla, el evento pasa a hecho solo.
 */
export function ModalIniciarVisita({
  idioma,
  propiedadId,
  plantillas,
  evento,
  onHecho,
  onCerrar,
}: {
  idioma: Idioma;
  propiedadId: string;
  plantillas: PlantillaResumen[];
  /** La visita de la agenda desde la que se empieza, si viene de ahi. */
  evento?: { id: string; titulo: string; motivo?: string | null } | undefined;
  /** Recibe la visita recien empezada: se entra directo en su pantalla. */
  onHecho: (ejecucionId: string) => void;
  onCerrar: () => void;
}) {
  const activas = plantillas.filter((p) => p.activo);
  /*
    Con una sola lista activa no hay nada que elegir: viene elegida. Obligar a
    abrir un desplegable de una opcion es la clase de paso que hace que la
    gente deje de usar la lista y acabe mandando un WhatsApp.
  */
  /*
    La cita programada POR UN MOTIVO se programo a proposito sin lista: no hay
    nada que recorrer, se va a abrirle al albanil. Colarle la unica lista activa
    porque es la unica seria darle veinte puntos que nadie pidio.
  */
  const [plantillaId, setPlantillaId] = useState(
    evento?.motivo ? '' : activas.length === 1 ? activas[0]!.id : '',
  );
  /*
    Si la cita se programo por un motivo concreto, ya esta escrito: aqui se lee
    y, si hace falta, se corrige. Teclearlo otra vez delante de la casa seria
    contestar lo que uno mismo contesto el lunes.
  */
  const [motivo, setMotivo] = useState(evento?.motivo ?? '');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
    SIN LISTA, EL MOTIVO ES OBLIGATORIO.

    Con lista, la lista ya dice que se iba a hacer. Sin lista no queda nada: la
    visita aparece en la ficha como una fecha y un nombre, y el mes siguiente
    nadie sabe a que se fue. Asi que o hay lista o hay motivo.
  */
  const faltaElMotivo = !plantillaId && motivo.trim().length < 2;

  return (
    <Modal idioma={idioma} titulo={t(idioma, 'visita.iniciar')} icono="reproducir" onCerrar={onCerrar}>
      <form
        className="formulario"
        style={{ maxWidth: 'none' }}
        onSubmit={(e) => {
          e.preventDefault();
          setEnviando(true);
          setError(null);
          void api
            .iniciarVisita(propiedadId, {
              ...(plantillaId ? { plantillaId } : {}),
              ...(evento ? { eventoId: evento.id } : {}),
              ...(motivo.trim() ? { notas: motivo.trim() } : {}),
            })
            .then(({ ejecucion }) => {
              onHecho(ejecucion.id);
              onCerrar();
            })
            .catch((e: unknown) =>
              setError(
                e instanceof ErrorApi ? t(idioma, e.mensajeClave) : t(idioma, 'error.servidor'),
              ),
            )
            .finally(() => setEnviando(false));
        }}
      >
        {evento && <p className="suave">{t(idioma, 'visita.desdeAgenda', { titulo: evento.titulo })}</p>}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        <div className="campo">
          <label htmlFor="vis-plantilla">{t(idioma, 'visita.plantilla')}</label>
          <select
            id="vis-plantilla"
            value={plantillaId}
            onChange={(e) => setPlantillaId(e.target.value)}
          >
            <option value="">{t(idioma, 'visita.sinPlantilla')}</option>
            {activas
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre} · {p.totalItems} {t(idioma, 'checklists.itemsCorto')}
                </option>
              ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="vis-motivo">
            {plantillaId ? t(idioma, 'visita.motivoOpcional') : t(idioma, 'visita.motivo')}
          </label>
          <textarea
            id="vis-motivo"
            rows={2}
            value={motivo}
            maxLength={2000}
            placeholder={t(idioma, 'visita.motivoPie')}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </div>
        <p className="suave" style={{ fontSize: '0.8125rem' }}>
          {t(idioma, 'visita.copiaNota')}
        </p>
        <button type="submit" className="acceso-entrar" disabled={enviando || faltaElMotivo}>
          <Icono nombre="reproducir" /> {t(idioma, 'visita.iniciar')}
        </button>
      </form>
    </Modal>
  );
}
